import { remove } from '../resource-base/APIVersion1';
import { parseRawData, useOnUnauthorized } from '../resource-base/adapters/AdapterUtils';
import { useMutationWithDelay } from '../resource-base/useMutationWithDelay';
import { GenericDeleteMutateObject, GenericDeleteMutateObjectSchema } from '../resource-base/mutateObjects';
import {
    MutationValidationResource,
    MutationValidationResourceSchema,
} from '../resources/validation/mutationValidationResource';
import {
    MutationErrorResponseSchema,
    MutationSuccessResponseSchema,
} from '../resources/validation/errorResponseResource';
import { showNotification } from '../../ui/util/showNotification';

export function useDeletionServiceActions() {
    const onUnauthorized = useOnUnauthorized();

    const processMutationResponse = async (result: Response): Promise<MutationValidationResource | null> => {
        if (result.status === 200 || result.status === 201) {
            return result
                .json()
                .then(rawData => parseRawData(rawData, MutationSuccessResponseSchema))
                .then(errorResponseResource => parseRawData(errorResponseResource.content, MutationValidationResourceSchema))
                .then(mutationValidationResource => {
                    if (mutationValidationResource.successful) {
                        showNotification({
                            severity: 'success',
                            title: `Successfully deleted resource`,
                            mutationValidationResult: mutationValidationResource,
                        });
                    } else if (!mutationValidationResource.successful) {
                        showNotification({
                            severity: 'error',
                            title: `Could not delete resource`,
                            mutationValidationResult: mutationValidationResource,
                        });
                    }
                    return mutationValidationResource;
                });
        } else if (result.status === 204) {
            showNotification({
                severity: 'info',
                title: 'No changes to update resource',
            });
        } else if (result.status === 401) {
            onUnauthorized(result, null).then();
        } else {
            result
                .json()
                .then(rawData => {
                    const parsed = MutationErrorResponseSchema.safeParse(rawData);
                    if (parsed.success) {
                        return parsed.data;
                    } else {
                        return parseRawData(rawData, MutationSuccessResponseSchema);
                    }
                })
                .then(value => {
                    showNotification({
                        severity: 'error',
                        title: `Could not delete resource(s)`,
                        errorResponseResult: value,
                    });
                });
        }
        return null;
    };

    const useDeleteResource = () => {
        return useMutationWithDelay(
            ({ id, persistableType }: GenericDeleteMutateObject) =>
                remove(`deletion/${id}?persistable-type=${persistableType}`).then(result => processMutationResponse(result)),
            GenericDeleteMutateObjectSchema,
        );
    };

    return {
        useDeleteResource,
    };
}
